import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react';
import { api, onAuthChange } from './api';

interface User {
  email: string;
  name?: string;
  alias?: string | null;
  groups: string[];
  authMethod: 'midway' | 'idprism';
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  canDownload: boolean;
  canDownloadRoc: boolean;
  accessMode: 'view' | 'download';
  requestStatus?: 'PENDING' | 'APPROVED' | 'DENIED';
  reviewerNotes?: string;
  requestedAt?: string;
  reviewedAt?: string;
  notifyNewDocuments: boolean;
  pendingReRequest: boolean;
  pendingRequestDetails?: {
    requestedDocuments: { reportId: string; accessMode: 'view' | 'download' }[];
    requestedAocReportIds: string[];
  };
  logout: () => void;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [canDownload, setCanDownload] = useState(false);
  const [canDownloadRoc, setCanDownloadRoc] = useState(false);
  const [accessMode, setAccessMode] = useState<'view' | 'download'>('download');
  const [isAdmin, setIsAdmin] = useState(false);
  const [requestStatus, setRequestStatus] = useState<'PENDING' | 'APPROVED' | 'DENIED' | undefined>(undefined);
  const [reviewerNotes, setReviewerNotes] = useState<string | undefined>(undefined);
  const [requestedAt, setRequestedAt] = useState<string | undefined>(undefined);
  const [reviewedAt, setReviewedAt] = useState<string | undefined>(undefined);
  const [notifyNewDocuments, setNotifyNewDocuments] = useState(true);
  const [pendingReRequest, setPendingReRequest] = useState(false);
  const [pendingRequestDetails, setPendingRequestDetails] = useState<{
    requestedDocuments: { reportId: string; accessMode: 'view' | 'download' }[];
    requestedAocReportIds: string[];
  } | undefined>(undefined);

  const refreshInFlight = useRef<Promise<void> | null>(null);

  const clearState = () => {
    setUser(null);
    setCanDownload(false);
    setCanDownloadRoc(false);
    setAccessMode('download');
    setIsAdmin(false);
    setRequestStatus(undefined);
    setReviewerNotes(undefined);
    setRequestedAt(undefined);
    setReviewedAt(undefined);
    setNotifyNewDocuments(true);
    setPendingReRequest(false);
    setPendingRequestDetails(undefined);
  };

  const refreshMe = async (): Promise<void> => {
    if (refreshInFlight.current) return refreshInFlight.current;
    const promise = (async () => {
      try {
        const me = await api.me();
        setUser({
          email: me.email,
          name: me.name,
          alias: me.alias,
          groups: [],
          authMethod: me.authMethod as 'midway' | 'idprism',
        });
        setCanDownload(me.canDownload);
        setCanDownloadRoc(me.canDownloadRoc);
        setAccessMode(me.accessMode || 'download');
        setIsAdmin(me.isAdmin);
        setRequestStatus(me.requestStatus);
        setReviewerNotes(me.reviewerNotes);
        setRequestedAt(me.requestedAt);
        setReviewedAt(me.reviewedAt);
        setNotifyNewDocuments(me.notifyNewDocuments !== false);
        setPendingReRequest(me.pendingReRequest || false);
        setPendingRequestDetails(me.pendingRequestDetails);
      } catch {
        clearState();
      } finally {
        refreshInFlight.current = null;
      }
    })();
    refreshInFlight.current = promise;
    return promise;
  };

  useEffect(() => {
    refreshMe().finally(() => setLoading(false));

    const unsubscribe = onAuthChange((state) => {
      if (state === 'unauthenticated') {
        clearState();
      }
    });

    return () => { unsubscribe(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── PCI DSS Req 8.2.8: Auto-logout after 15 minutes of inactivity ──
  // Uses BroadcastChannel for cross-tab coordination (no localStorage for
  // sensitive session data).
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastLocalActivity = useRef<number>(Date.now());
  const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000;

  useEffect(() => {
    if (!user) return;

    const channel = new BroadcastChannel('pci_portal_activity');

    const resetTimer = () => {
      lastLocalActivity.current = Date.now();
      channel.postMessage({ ts: lastLocalActivity.current });
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      inactivityTimer.current = setTimeout(() => {
        const elapsed = Date.now() - lastLocalActivity.current;
        if (elapsed < INACTIVITY_TIMEOUT_MS) {
          inactivityTimer.current = setTimeout(() => resetTimer(), INACTIVITY_TIMEOUT_MS - elapsed);
          return;
        }
        logout();
        window.location.href = '/login?reason=inactivity';
      }, INACTIVITY_TIMEOUT_MS);
    };

    channel.onmessage = (e) => {
      if (e.data?.ts > lastLocalActivity.current) {
        lastLocalActivity.current = e.data.ts;
        resetTimer();
      }
    };

    const events: (keyof WindowEventMap)[] = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      events.forEach((e) => window.removeEventListener(e, resetTimer));
      channel.close();
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const logout = () => {
    clearState();
    void api.logout();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAdmin,
        canDownload,
        canDownloadRoc,
        accessMode,
        requestStatus,
        reviewerNotes,
        requestedAt,
        reviewedAt,
        notifyNewDocuments,
        pendingReRequest,
        pendingRequestDetails,
        logout,
        refreshMe,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
